/* Le handicap, et ce qu'il rend trou par trou.
 *
 * ── Deux nombres qu'on confond souvent ──────────────────────────────────────
 *
 * L'INDEX est la valeur du joueur, la même partout dans le monde : 28,4. Il ne
 * dit pas combien de coups on reçoit aujourd'hui, parce qu'un parcours n'a pas
 * la même difficulté qu'un autre — ni le même départ.
 *
 * Le COUPS REÇUS (course handicap) est ce que l'index devient SUR CE PARCOURS,
 * DEPUIS CE DÉPART :
 *
 *     coups reçus = index × (slope / 113) + (SSS − par)
 *
 * Le slope mesure combien le parcours punit un joueur moyen par rapport à un
 * très bon joueur ; 113 est le slope de référence, celui d'un parcours neutre.
 * Le SSS (course rating) est le score qu'un joueur de référence y rend : quand
 * il dépasse le par, le parcours est plus dur que sa carte ne le dit.
 *
 * ── Ce qu'on fait quand le carnet est incomplet ─────────────────────────────
 *
 * Beaucoup de carnets n'ont ni slope ni SSS saisis. Plutôt qu'un refus, la
 * formule retombe sur le cas NEUTRE — slope 113, SSS égal au par — où elle se
 * réduit à « coups reçus = index ». Ce n'est pas un contournement : c'est la
 * même formule, avec les valeurs d'un parcours de référence. L'écran dit que le
 * calcul est approché, pour qu'on sache quoi corriger.
 *
 * ── Neuf trous ──────────────────────────────────────────────────────────────
 *
 * On reçoit la moitié des coups, arrondie. La règle est bien de diviser le
 * résultat final, et non l'index : le slope et le SSS d'un neuf trous sont déjà
 * ceux de ses neuf trous.
 */

/** Ce qu'un départ dit de la difficulté du parcours. Tout est facultatif : un
 *  carnet incomplet doit pouvoir servir quand même. */
export interface DifficulteDepart {
    /** Slope du départ. 113 = parcours de référence. */
    slope?: number | null;
    /** SSS / course rating du départ, dans la même unité que le par. */
    courseRating?: number | null;
    /** Le par total du parcours, tel que le carnet l'additionne. */
    par?: number | null;
}

/** Les coups reçus sur ce parcours, depuis ce départ.
 *
 *  `null` si le joueur n'a pas d'index : on ne devine pas un handicap.
 *
 *  ── Le PLAFOND ───────────────────────────────────────────────
 *
 *  La formule n'en connait pas ; l'usage en applique un. La plupart des
 *  compétitions françaises limitent le handicap de jeu à 36, et les applications
 *  officielles affichent cette limite : un index 54 reçoit alors 36 coups, deux
 *  par trou et non trois.
 *
 *  Il arrive donc de l'extérieur, et n'est pas écrit ici : c'est une convention
 *  — elle dépend de la fédération, du club, parfois du format — là où la
 *  formule est une règle. Absent, rien n'est plafonné.
 *
 *  Il s'applique AVANT la division par deux des neuf trous : il borne le
 *  handicap de jeu sur dix-huit, dont neuf trous prennent la moitié.
 *
 *  @param nbTrous 9 ou 18. Sur neuf trous, on reçoit la moitié des coups.
 *  @param maximum Plafond du handicap de jeu sur dix-huit trous. */
export function coupsRecus(
    index: number | null | undefined,
    depart: DifficulteDepart = {},
    nbTrous = 18,
    maximum?: number | null,
): number | null {
    if (index == null || !Number.isFinite(index)) return null;
    const slope = depart.slope != null && depart.slope > 0 ? depart.slope : 113;
    // Sans SSS, on prend le par : l'écart est alors nul, ce qui est le cas
    // neutre et non une valeur inventée.
    const ecartRating = depart.courseRating != null && depart.par != null
        ? depart.courseRating - depart.par
        : 0;
    const brut = index * (slope / 113) + ecartRating;
    const borne = maximum != null && maximum > 0 ? Math.min(brut, maximum) : brut;
    const surLeTour = nbTrous <= 9 ? borne / 2 : borne;
    return Math.round(surLeTour);
}

/** Combien de coups sont rendus sur CE trou.
 *
 *  Les coups se répartissent par INDEX DE DIFFICULTÉ : le trou d'index 1 est le
 *  plus dur, il sert le premier. Au-delà d'un tour complet, chaque trou reçoit
 *  un coup de plus et le reste redescend par les index les plus bas — c'est
 *  ainsi qu'un joueur à 30 reçoit deux coups sur les douze trous les plus durs.
 *
 *  Un index de trou absent rend `null` : sans lui on ne sait pas dans quel
 *  ordre servir, et répartir au hasard donnerait une carte fausse qui aurait
 *  l'air juste.
 *
 *  Un handicap NÉGATIF — le joueur rend des coups au parcours — n'est pas
 *  réparti : ces coups se reprennent en commençant par les trous les plus
 *  faciles, une règle que personne n'applique en partie amicale. On rend 0.
 */
export function coupsRendusSurTrou(
    coupsDuTour: number | null,
    strokeIndex: number | null | undefined,
    nbTrous = 18,
): number | null {
    if (coupsDuTour == null) return null;
    if (strokeIndex == null || strokeIndex < 1) return null;
    if (coupsDuTour <= 0) return 0;
    const tours = Math.floor(coupsDuTour / nbTrous);
    const reste = coupsDuTour % nbTrous;
    return tours + (strokeIndex <= reste ? 1 : 0);
}

/** Le score NET d'un trou : les coups joués, moins ceux qui sont rendus.
 *
 *  `null` dès qu'un des deux manque — un net calculé sur un trou non joué, ou
 *  sur un trou sans index, serait un chiffre qui ment. */
export function netTrou(
    coups: number | null | undefined,
    rendus: number | null,
): number | null {
    if (coups == null || rendus == null) return null;
    return coups - rendus;
}
